import React, { useEffect, useState, useMemo } from 'react';
import API from '../api';
import { toast } from 'react-toastify';
import { usePharmacy } from '../context/PharmacyContext';

export default function NearExpiryPage() {
  const getCache = (key, defaultVal) => {
    const cached = sessionStorage.getItem(`nearExpiry_${key}`);
    return cached ? JSON.parse(cached) : defaultVal;
  };

  const [medicines, setMedicines] = useState(() => getCache('medicines', []));
  const [search, setSearch] = useState(() => getCache('search', ''));
  const [loading, setLoading] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [filterStatus, setFilterStatus] = useState(() => getCache('filterStatus', 'ALL'));

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);

  const { pharmacyDetails } = usePharmacy();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [daysFilter, setDaysFilter] = useState(() => getCache('daysFilter', '365'));
  const [parties, setParties] = useState(() => getCache('parties', []));

  const [returnModalMed, setReturnModalMed] = useState(null);
  const [returnQty, setReturnQty] = useState('');
  const [returnPrice, setReturnPrice] = useState('');
  const [returnParty, setReturnParty] = useState('');
  const [returnTaxPercent, setReturnTaxPercent] = useState('0');
  const [returnNotes, setReturnNotes] = useState('');
  const [submittingReturn, setSubmittingReturn] = useState(false);

  const fetchParties = async () => {
    try {
      const { data } = await API.get('/parties');
      setParties(data);
      sessionStorage.setItem('nearExpiry_parties', JSON.stringify(data));
    } catch (e) {
      console.error(e);
    }
  };

  const fetchNearExpiry = async () => {
    try {
      if (!sessionStorage.getItem('nearExpiry_medicines')) setLoading(true);
      const { data } = await API.get('/medicines/near-expiry', { params: { days: daysFilter } });
      setMedicines(data);
      sessionStorage.setItem('nearExpiry_medicines', JSON.stringify(data));
    } catch (error) {
      console.error('Error fetching near expiry medicines:', error);
      toast.error('Failed to load medicines');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNearExpiry();
    if (parties.length === 0) fetchParties();
  }, [daysFilter]);

  useEffect(() => {
    sessionStorage.setItem('nearExpiry_search', JSON.stringify(search));
  }, [search]);

  useEffect(() => {
    sessionStorage.setItem('nearExpiry_filterStatus', JSON.stringify(filterStatus));
  }, [filterStatus]);

  useEffect(() => {
    sessionStorage.setItem('nearExpiry_daysFilter', JSON.stringify(daysFilter));
  }, [daysFilter]);

  const selectedReturnSupplier = useMemo(() => {
    return parties.find(p => p._id === returnParty) || null;
  }, [returnParty, parties]);

  const isReturnVatAllowed = selectedReturnSupplier?.registrationType === 'VAT Registered';

  useEffect(() => {
    if (selectedReturnSupplier) {
      if (isReturnVatAllowed) {
        setReturnTaxPercent('13');
      } else {
        setReturnTaxPercent('0');
      }
    }
  }, [selectedReturnSupplier, isReturnVatAllowed]);

  const fmt = (num) =>
    Number.isFinite(Number(num))
      ? Number(num).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '0.00';

  const returnSubtotal = (Number(returnQty) || 0) * (Number(returnPrice) || 0);
  const returnTaxAmount = returnSubtotal * (Number(returnTaxPercent) / 100);
  const returnGrandTotal = returnSubtotal + returnTaxAmount;

  const generateReturnReport = async (returnData, medicineData, supplierData, returnId) => {
    const today = new Date();
    const expiryInfo = getExpiryStatus(medicineData.expiryDate);
    
    const reportHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Purchase Return Request - ${returnData.returnInvoiceNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Arial', sans-serif; 
            padding: 20px; 
            color: #1a202c; 
            line-height: 1.4;
            font-size: 14px;
            max-width: 210mm;
            margin: 0 auto;
          }
          .header { 
            text-align: center; 
            border-bottom: 3px solid #1880c2; 
            padding-bottom: 15px; 
            margin-bottom: 20px; 
          }
          .header h1 { 
            margin: 0; 
            color: #1880c2; 
            font-size: 24px;
            font-weight: bold;
            text-transform: uppercase;
          }
          .header p { 
            margin: 3px 0; 
            color: #64748b; 
            font-size: 12px;
          }
          .return-header {
            background: #f8fafc;
            border: 2px solid #1880c2;
            border-radius: 8px;
            padding: 15px;
            margin-bottom: 20px;
            text-align: center;
          }
          .return-header h2 {
            margin: 0 0 10px 0;
            color: #1880c2;
            font-size: 20px;
            text-transform: uppercase;
          }
          .return-details {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
            font-size: 13px;
            font-weight: 600;
          }
          .info-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 20px;
          }
          .info-card {
            background: #ffffff;
            border: 2px solid #e2e8f0;
            border-radius: 8px;
            padding: 15px;
          }
          .info-card h3 {
            margin: 0 0 10px 0;
            color: #1e293b;
            font-size: 14px;
            text-transform: uppercase;
            border-bottom: 2px solid #e2e8f0;
            padding-bottom: 5px;
          }
          .info-card p {
            margin: 4px 0;
            font-size: 13px;
            color: #334155;
            line-height: 1.3;
          }
          .medicine-details {
            background: #fef3c7;
            border: 2px solid #f59e0b;
            border-radius: 8px;
            padding: 15px;
            margin-bottom: 20px;
          }
          .medicine-details h2 {
            margin: 0 0 12px 0;
            color: #92400e;
            font-size: 16px;
            text-transform: uppercase;
          }
          .med-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
            font-size: 13px;
          }
          .med-item {
            display: flex;
            justify-content: space-between;
            padding: 5px 0;
            border-bottom: 1px solid #f4d03f;
          }
          .amount-summary {
            background: #ecfdf5;
            border: 2px solid #10b981;
            border-radius: 8px;
            padding: 15px;
            margin-bottom: 20px;
          }
          .amount-summary h2 {
            margin: 0 0 12px 0;
            color: #065f46;
            font-size: 16px;
            text-transform: uppercase;
          }
          .amount-row {
            display: flex;
            justify-content: space-between;
            margin: 8px 0;
            font-size: 13px;
            padding: 5px 0;
          }
          .amount-total {
            border-top: 2px solid #065f46;
            padding-top: 10px;
            margin-top: 10px;
            font-weight: bold;
            font-size: 16px;
          }
          .important-notes {
            background: #fee2e2;
            border: 2px solid #fecaca;
            border-radius: 8px;
            padding: 15px;
            margin-bottom: 20px;
          }
          .important-notes h3 {
            color: #dc2626;
            margin-bottom: 10px;
            font-size: 14px;
          }
          .important-notes p {
            font-size: 12px;
            color: #dc2626;
            line-height: 1.4;
            margin: 5px 0;
          }
          .signatures {
            margin-top: 30px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 40px;
          }
          .signature-box {
            text-align: center;
            border-top: 2px solid #1e293b;
            padding-top: 10px;
            font-size: 12px;
            color: #64748b;
            height: 60px;
          }
          .footer { 
            margin-top: 30px; 
            text-align: center; 
            font-size: 10px; 
            color: #718096; 
            border-top: 2px solid #e2e8f0;
            padding-top: 15px;
          }
          @media print {
            body { padding: 15px; font-size: 13px; }
            .header h1 { font-size: 20px; }
            .signatures { margin-top: 20px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${pharmacyDetails.name}</h1>
          ${pharmacyDetails.address ? `<p><strong>Address:</strong> ${pharmacyDetails.address}</p>` : ''}
          <p>
            ${pharmacyDetails.phone ? `<strong>Phone:</strong> ${pharmacyDetails.phone} | ` : ''}
            ${pharmacyDetails.pan ? `<strong>PAN:</strong> ${pharmacyDetails.pan}` : ''}
          </p>
        </div>

        <div class="return-header">
          <h2>Purchase Return Request (Debit Note)</h2>
          <div class="return-details">
            <div><strong>Return No:</strong> ${returnData.returnInvoiceNumber}</div>
            <div><strong>Date:</strong> ${today.toLocaleDateString('en-IN')}</div>
            <div><strong>Status:</strong> <span style="color: #f59e0b; font-weight: bold;">PENDING APPROVAL</span></div>
          </div>
        </div>

        <div class="info-section">
          <div class="info-card">
            <h3>Supplier Details</h3>
            <p><strong>Company Name:</strong> ${supplierData.name}</p>
            <p><strong>Tax Profile:</strong> ${supplierData.registrationType || 'Unregistered'}</p>
            ${supplierData.panNumber ? `<p><strong>PAN/VAT No:</strong> ${supplierData.panNumber}</p>` : ''}
          </div>
          
          <div class="info-card">
            <h3>Return Information</h3>
            <p><strong>Return Quantity:</strong> ${returnData.quantity} ${medicineData.quantityUnit || 'units'}</p>
            <p><strong>Unit Cost Price:</strong> Rs ${fmt(returnData.returnPrice)}</p>
            <p><strong>Reason:</strong> ${returnData.notes || 'Expiry Return'}</p>
          </div>
        </div>

        <div class="medicine-details">
          <h2>Medicine Information</h2>
          <div class="med-grid">
            <div class="med-item">
              <span><strong>Medicine Name:</strong></span>
              <span>${medicineData.name}</span>
            </div>
            <div class="med-item">
              <span><strong>Brand:</strong></span>
              <span>${medicineData.brand || 'N/A'}</span>
            </div>
            <div class="med-item">
              <span><strong>Batch Number:</strong></span>
              <span>${medicineData.batchNumber || 'N/A'}</span>
            </div>
            <div class="med-item">
              <span><strong>Expiry Date:</strong></span>
              <span>${new Date(medicineData.expiryDate).toLocaleDateString('en-IN')}</span>
            </div>
          </div>
        </div>

        <div class="amount-summary">
          <h2>Financial Summary</h2>
          <div class="amount-row">
            <span>Base Subtotal:</span>
            <span>Rs ${fmt(returnData.subtotal)}</span>
          </div>
          <div class="amount-row">
            <span>VAT/Tax Reversal (${returnData.taxPercent}%):</span>
            <span>Rs ${fmt(returnData.taxAmount)}</span>
          </div>
          <div class="amount-row amount-total">
            <span>TOTAL CREDIT REQUESTED:</span>
            <span>Rs ${fmt(returnData.totalGrand)}</span>
          </div>
        </div>

        <div class="important-notes">
          <h3>IMPORTANT INSTRUCTIONS</h3>
          <p><strong>1. Verification:</strong> Please verify the physical condition and expiry date of returned medicines.</p>
          <p><strong>2. Credit Note:</strong> If approved, please issue a credit note for the specified amount including VAT reversal if applicable.</p>
        </div>

        <div class="signatures">
          <div class="signature-box">
            <strong>PHARMACY AUTHORIZED SIGNATURE</strong><br>
            <small>Return Initiated By: ${pharmacyDetails.name}</small>
          </div>
          <div class="signature-box">
            <strong>SUPPLIER AUTHORIZED SIGNATURE</strong><br>
            <small>Return Received & Acknowledged</small>
          </div>
        </div>

        <div class="footer">
          <p><strong>Return Reference:</strong> ${returnData.returnInvoiceNumber} | Generated: ${today.toLocaleString('en-IN')}</p>
          <p>This document serves as an official return request and should accompany the returned medicines</p>
        </div>
      </body>
      </html>
    `;

    if (returnId) {
      try {
        await API.post('/purchase-returns/save-report', {
          returnId,
          reportHtml
        });
      } catch (error) {
        console.error('Error saving report:', error);
      }
    }

    const printWindow = window.open('', '_blank');
    printWindow.document.write(reportHtml);
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const openReturnModal = (med, e) => {
    if (e) e.stopPropagation();
    setReturnModalMed(med);
    setReturnQty(med.quantity ? med.quantity.toString() : '1');
    
    const autoPrice = med.purchasePricePerUnit || med.purchasePrice || med.costPrice || med.sellingPricePerUnit || med.mrpPerUnit || 0;
    
    setReturnPrice(autoPrice.toString());
    setReturnParty(med.party?._id || med.party || '');
    setReturnNotes(`Purchase Return: Expired/near-expiry stock batch ${med.batchNumber || ''}`);
  };

  const handleConfirmReturn = async (e) => {
    e.preventDefault();
    if (!returnModalMed) return;

    const qtyNum = Number(returnQty);
    if (!qtyNum || qtyNum <= 0) return toast.warning('Enter a valid return quantity');
    if (qtyNum > returnModalMed.quantity) return toast.warning(`Cannot return more than available stock (${returnModalMed.quantity})`);
    if (!selectedReturnSupplier) return toast.warning('Please select a supplier');

    try {
      setSubmittingReturn(true);
      
      const returnRequestData = {
        batchId: returnModalMed._id,
        medicineId: returnModalMed.medicineId || returnModalMed._id,
        partyId: returnParty,
        quantity: qtyNum,
        returnPrice: Number(returnPrice),
        taxAmount: returnTaxAmount,
        notes: returnNotes
      };

      const res = await API.post('/purchase-returns', returnRequestData);
      
      const returnData = {
        returnInvoiceNumber: res.data.purchaseReturn?.returnInvoiceNumber,
        quantity: qtyNum,
        returnPrice: Number(returnPrice),
        subtotal: returnSubtotal,
        taxPercent: returnTaxPercent,
        taxAmount: returnTaxAmount,
        totalGrand: returnGrandTotal,
        notes: returnNotes
      };

      await generateReturnReport(
        returnData, 
        returnModalMed, 
        selectedReturnSupplier, 
        res.data.purchaseReturn?._id
      );

      toast.success('🎉 Return Initiated & Report Saved! Check Returns History to reprint.');
      setReturnModalMed(null);
      fetchNearExpiry();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to process return');
    } finally {
      setSubmittingReturn(false);
    }
  };

  const getExpiryStatus = (expiryDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);
    
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return { 
        status: 'expired', color: '#dc2626', bg: '#fef2f2', border: '#fecaca',
        text: `Expired ${Math.abs(daysUntilExpiry)}d ago`, shortText: 'Expired', icon: '❌', days: daysUntilExpiry 
      };
    } else if (daysUntilExpiry === 0) {
      return { 
        status: 'expired-today', color: '#dc2626', bg: '#fef2f2', border: '#fecaca',
        text: 'Expires Today!', shortText: 'Today!', icon: '🚨', days: 0
      };
    } else if (daysUntilExpiry <= 7) {
      return { 
        status: 'critical', color: '#dc2626', bg: '#fef2f2', border: '#fecaca',
        text: `${daysUntilExpiry} days left`, shortText: `${daysUntilExpiry}d`, icon: '🚨', days: daysUntilExpiry 
      };
    } else if (daysUntilExpiry <= 30) {
      return { 
        status: 'warning', color: '#d97706', bg: '#fffbeb', border: '#fde68a',
        text: `${daysUntilExpiry} days left`, shortText: `${daysUntilExpiry}d`, icon: '⚠️', days: daysUntilExpiry 
      };
    } else if (daysUntilExpiry <= 90) {
      return { 
        status: 'caution', color: '#1880c2', bg: '#e0f2fe', border: '#bae6fd',
        text: `${daysUntilExpiry} days left`, shortText: `${daysUntilExpiry}d`, icon: '⏳', days: daysUntilExpiry 
      };
    } else {
      return { 
        status: 'normal', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0',
        text: `${daysUntilExpiry} days left`, shortText: `${daysUntilExpiry}d`, icon: '✓', days: daysUntilExpiry 
      };
    }
  };

  const expiredMeds = medicines.filter(m => {
    const status = getExpiryStatus(m.expiryDate).status;
    return status === 'expired' || status === 'expired-today';
  });
  const criticalMeds = medicines.filter(m => getExpiryStatus(m.expiryDate).status === 'critical');
  const warningMeds = medicines.filter(m => getExpiryStatus(m.expiryDate).status === 'warning');
  const cautionMeds = medicines.filter(m => getExpiryStatus(m.expiryDate).status === 'caution');

  let filteredMedicines = medicines.filter(m => 
    m.name.toLowerCase().includes(search.toLowerCase()) ||
    m.brand?.toLowerCase().includes(search.toLowerCase()) ||
    (m.party?.name?.toLowerCase().includes(search.toLowerCase()) ?? false)
  );

  if (filterStatus !== 'ALL') {
    filteredMedicines = filteredMedicines.filter(m => {
      const status = getExpiryStatus(m.expiryDate).status;
      if (filterStatus === 'EXPIRED') return status === 'expired' || status === 'expired-today';
      if (filterStatus === 'CRITICAL') return status === 'critical';
      if (filterStatus === 'WARNING') return status === 'warning';
      if (filterStatus === 'CAUTION') return status === 'caution';
      return true;
    });
  }

  filteredMedicines.sort((a, b) => new Date(a.expiryDate) - new Date(b.expiryDate));

  const styles = {
    container: { minHeight: '100vh', background: '#f4f9fd', fontFamily: "'Inter', sans-serif" },
    header: { 
      background: '#ffffff', borderBottom: '1px solid #e0f2fe', padding: isMobile ? '16px' : '20px 32px', 
      display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', 
      alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 16 : 0, position: 'sticky', top: 0, zIndex: 100 
    },
    headerTitle: { display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12 },
    headerIcon: { 
      width: isMobile ? 36 : 40, height: isMobile ? 36 : 40, background: 'linear-gradient(135deg, #1880c2 0%, #0369a1 100%)', 
      borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 18 
    },
    headerText: { fontSize: isMobile ? 18 : 20, fontWeight: 700, color: '#1e293b' },
    headerSubtext: { fontSize: 13, color: '#64748b' },
    headerActions: { display: 'flex', alignItems: 'center', gap: 10, width: isMobile ? '100%' : 'auto', flexWrap: 'wrap' },
    main: { maxWidth: 1400, margin: '0 auto', padding: isMobile ? '16px' : '24px 32px' },
    statsRow: { display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: 16, marginBottom: 24 },
    statCard: { 
      background: '#ffffff', borderRadius: 12, padding: isMobile ? 16 : 20, border: '1px solid #e0f2fe', 
      display: 'flex', gap: 16, alignItems: 'center', cursor: 'pointer', transition: 'all 0.2s'
    },
    statCardActive: { boxShadow: '0 0 0 2px #1880c2', borderColor: '#1880c2' },
    statIconBox: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 },
    statContent: { flex: 1 },
    statNumber: { fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#1e293b' },
    statTitle: { fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' },
    card: { background: '#ffffff', borderRadius: 12, border: '1px solid #e0f2fe', marginBottom: 24 },
    cardHeader: { padding: '20px 24px', borderBottom: '1px solid #e0f2fe', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, background: '#f8fafc' },
    cardTitle: { fontSize: 16, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10, margin: 0 },
    cardBody: { padding: isMobile ? 16 : 24 },
    formGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr auto', gap: 16, alignItems: 'center' },
    input: { padding: '12px 14px', paddingLeft: 44, borderRadius: 8, border: '1px solid #e1e8ed', fontSize: 14, width: '100%', boxSizing: 'border-box', background: '#fff', outline: 'none' },
    select: { padding: '12px 14px', borderRadius: 8, border: '1px solid #e1e8ed', fontSize: 14, minWidth: 180, boxSizing: 'border-box', background: '#fff', cursor: 'pointer', fontWeight: 600, outline: 'none' },
    btnSecondary: { padding: '10px 20px', background: '#ffffff', color: '#475569', border: '1px solid #e0f2fe', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { padding: '14px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#64748b', background: '#f4f9fd', borderBottom: '1px solid #e0f2fe', textTransform: 'uppercase', whiteSpace: 'nowrap' },
    td: { padding: '16px', fontSize: 14, color: '#1e293b', borderBottom: '1px solid #f4f9fd' },
    badge: { padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 },
    modal: { position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: isMobile ? 0 : 20 },
    modalContent: { background: '#ffffff', borderRadius: isMobile ? 0 : 16, width: isMobile ? '100%' : 'min(500px, 95%)', maxHeight: isMobile ? '100%' : '90vh', overflowY: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.25)' },
    modalHeader: { padding: '24px', borderBottom: '1px solid #e0f2fe', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    modalBody: { padding: '24px' },
    mobileCard: { background: '#fff', borderRadius: 12, border: '1px solid #e0f2fe', padding: 16, marginBottom: 12, cursor: 'pointer', transition: 'all 0.2s' },
    emptyState: { textAlign: 'center', padding: 60, color: '#64748b' },
    tipBanner: { marginTop: 24, padding: '16px 20px', background: '#e0f2fe', borderLeft: '4px solid #1880c2', borderRadius: 8, color: '#0369a1', fontSize: 14 },
    skeleton: { background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', borderRadius: 6 },
    searchWrapper: { position: 'relative', flex: 1 },
    searchIcon: { position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 18, color: '#94a3b8' }
  };

  const SkeletonStatCard = () => (
    <div style={styles.statCard}>
      <div style={{ ...styles.skeleton, width: 48, height: 48, borderRadius: 12 }}></div>
      <div style={{ flex: 1 }}>
        <div style={{ ...styles.skeleton, height: 24, width: '60%', marginBottom: 8 }}></div>
        <div style={{ ...styles.skeleton, height: 14, width: '80%' }}></div>
      </div>
    </div>
  );

  const SkeletonTableRow = () => (
    <tr>
      <td style={styles.td}><div style={{ ...styles.skeleton, height: 18, width: '70%', marginBottom: 4 }}></div><div style={{ ...styles.skeleton, height: 14, width: '40%' }}></div></td>
      <td style={styles.td}><div style={{ ...styles.skeleton, height: 16, width: 80 }}></div></td>
      <td style={styles.td}><div style={{ ...styles.skeleton, height: 16, width: 70 }}></div></td>
      <td style={styles.td}><div style={{ ...styles.skeleton, height: 16, width: 100 }}></div></td>
      <td style={{ ...styles.td, textAlign: 'right' }}><div style={{ ...styles.skeleton, height: 16, width: 60, marginLeft: 'auto' }}></div></td>
      <td style={{ ...styles.td, textAlign: 'right' }}><div style={{ ...styles.skeleton, height: 16, width: 50, marginLeft: 'auto' }}></div></td>
      <td style={styles.td}><div style={{ ...styles.skeleton, height: 16, width: 90 }}></div></td>
      <td style={{ ...styles.td, textAlign: 'center' }}><div style={{ ...styles.skeleton, height: 16, width: 30, margin: '0 auto' }}></div></td>
      <td style={{ ...styles.td, textAlign: 'center' }}><div style={{ ...styles.skeleton, height: 28, width: 100, margin: '0 auto', borderRadius: 6 }}></div></td>
    </tr>
  );

  const SkeletonMobileCard = () => (
    <div style={styles.mobileCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ flex: 1 }}><div style={{ ...styles.skeleton, height: 18, width: '60%', marginBottom: 6 }}></div><div style={{ ...styles.skeleton, height: 14, width: '40%' }}></div></div>
        <div style={{ ...styles.skeleton, height: 28, width: 70, borderRadius: 6 }}></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 12 }}>
        <div style={{ ...styles.skeleton, height: 50, borderRadius: 8 }}></div><div style={{ ...styles.skeleton, height: 50, borderRadius: 8 }}></div>
      </div>
    </div>
  );

  const StatusBadge = ({ expiryDate, showFull = false }) => {
    const info = getExpiryStatus(expiryDate);
    return (
      <span style={{ ...styles.badge, background: info.bg, color: info.color, border: `1px solid ${info.border}` }}>
        {info.icon} {showFull ? info.text : info.shortText}
      </span>
    );
  };

  const MedicineCard = ({ medicine }) => {
    const expiryInfo = getExpiryStatus(medicine.expiryDate);
    const isLowStock = medicine.quantity < (medicine.minStockLevel || 10);
    const isExpiredCard = expiryInfo.status === 'expired' || expiryInfo.status === 'expired-today' || expiryInfo.status === 'critical';

    return (
      <div 
        style={{ ...styles.mobileCard, ...(isExpiredCard ? { background: '#fef2f2', borderColor: '#fecaca' } : {}) }}
        onClick={() => setSelectedMedicine(medicine)}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{medicine.name}</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{medicine.brand || medicine.category} {medicine.strength && `• ${medicine.strength}`}</div>
          </div>
          <StatusBadge expiryDate={medicine.expiryDate} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 12 }}>
          <div style={{ padding: 10, background: '#f8fafc', borderRadius: 8 }}>
            <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Price</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>Rs{fmt(medicine.sellingPricePerUnit || 0)}</div>
          </div>
          <div style={{ padding: 10, background: '#f8fafc', borderRadius: 8 }}>
            <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Stock</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: isLowStock ? '#dc2626' : '#1e293b' }}>
              {medicine.quantity} {medicine.quantityUnit} {isLowStock && '⚠️'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid #e0f2fe', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#64748b' }}>Exp: {new Date(medicine.expiryDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: expiryInfo.color }}>{expiryInfo.days < 0 ? `${Math.abs(expiryInfo.days)}d ago` : `${expiryInfo.days}d left`}</span>
            <button
              type="button"
              onClick={(e) => openReturnModal(medicine, e)}
              style={{ padding: '4px 10px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
            >
              ↩️ Return
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handlePrintExpiryList = () => {
    const printWindow = window.open('', '_blank');
    const rows = filteredMedicines.map((m, i) => {
      const expInfo = getExpiryStatus(m.expiryDate);
      return `
        <tr>
          <td style="padding:6px; border:1px solid #e0f2fe;">${i + 1}</td>
          <td style="padding:6px; border:1px solid #e0f2fe; font-weight:bold;">${m.name} ${m.strength ? `(${m.strength})` : ''}</td>
          <td style="padding:6px; border:1px solid #e0f2fe;">${m.brand || '-'}</td>
          <td style="padding:6px; border:1px solid #e0f2fe;">${m.batchNumber || '-'}</td>
          <td style="padding:6px; border:1px solid #e0f2fe;">${m.rackLocation || m.rackNumber || 'N/A'}</td>
          <td style="padding:6px; border:1px solid #e0f2fe;">${m.quantity} ${m.quantityUnit || ''}</td>
          <td style="padding:6px; border:1px solid #e0f2fe; font-weight:bold;">${new Date(m.expiryDate).toLocaleDateString('en-IN')}</td>
          <td style="padding:6px; border:1px solid #e0f2fe;">${m.party?.name || 'Supplier N/A'}</td>
          <td style="padding:6px; border:1px solid #e0f2fe; font-weight:bold; color:${expInfo.color};">${expInfo.shortText}</td>
        </tr>
      `;
    }).join('');

    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Near-Expiry & Expired Medicines List</title>
        <style>
          body { font-family: sans-serif; padding: 20px; color: #1a202c; }
          .header { text-align: center; border-bottom: 2px solid #1880c2; padding-bottom: 10px; margin-bottom: 20px; }
          .header h2 { margin: 0; color: #1880c2; text-transform: uppercase; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th { background: #1880c2; color: #fff; padding: 6px; text-align: left; }
          .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #718096; }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>Expiry & Return Claims Report</h2>
          <p style="margin:4px 0; font-size:12px; color:#64748b;">Generated: ${new Date().toLocaleString('en-IN')} | Total Medicines: ${filteredMedicines.length}</p>
        </div>
        <table>
          <thead>
            <tr>
              <th>#</th><th>Medicine Name</th><th>Brand</th><th>Batch #</th><th>Rack Location</th><th>Stock Qty</th><th>Expiry Date</th><th>Supplier</th><th>Status</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="footer"><p>Pharmacy Inventory Audit | Handover Document for Vendor Return</p></div>
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  };

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      <div style={styles.container}>
        <header style={styles.header}>
          <div style={styles.headerTitle}>
            <div style={styles.headerIcon}>⏰</div>
            <div>
              <div style={styles.headerText}>Near Expiry Alert</div>
              <div style={styles.headerSubtext}>Monitor medicines expiring within 90 days</div>
            </div>
          </div>
          <div style={styles.headerActions}>
            <div style={{ 
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', 
              background: expiredMeds.length > 0 ? '#fef2f2' : '#f0f0f4', color: expiredMeds.length > 0 ? '#dc2626' : '#22c55e', 
              borderRadius: 20, fontSize: 13, fontWeight: 600 
            }}>
              <span>{expiredMeds.length > 0 ? '⚠️' : '✅'}</span>
              {expiredMeds.length > 0 ? `${expiredMeds.length} Expired` : 'All Good'}
            </div>
            <select value={daysFilter} onChange={e => setDaysFilter(e.target.value)} style={{ ...styles.select, minWidth: 150, padding: '8px 12px', fontSize: 13 }}>
              <option value="30">Within 30 Days</option><option value="60">Within 60 Days</option><option value="90">Within 90 Days</option><option value="180">Within 180 Days</option><option value="365">Within 1 Year (All)</option>
            </select>
            <button onClick={handlePrintExpiryList} style={{ ...styles.btnSecondary, background: '#1880c2', color: '#fff', borderColor: '#1880c2' }}>
              🖨️ Print Expiry List
            </button>
            <button onClick={fetchNearExpiry} style={styles.btnSecondary}>🔄 Refresh</button>
          </div>
        </header>

        <main style={styles.main}>
          <div style={styles.statsRow}>
            {loading ? (
              <>
                <SkeletonStatCard /><SkeletonStatCard /><SkeletonStatCard /><SkeletonStatCard />{!isMobile && <SkeletonStatCard />}
              </>
            ) : (
              <>
                <div style={{ ...styles.statCard, ...(filterStatus === 'EXPIRED' ? styles.statCardActive : {}) }} onClick={() => setFilterStatus(filterStatus === 'EXPIRED' ? 'ALL' : 'EXPIRED')}>
                  <div style={{ ...styles.statIconBox, background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}><span style={{ color: 'white' }}>❌</span></div>
                  <div style={styles.statContent}><div style={{ ...styles.statNumber, color: '#dc2626' }}>{expiredMeds.length}</div><div style={styles.statTitle}>Expired</div></div>
                </div>
                <div style={{ ...styles.statCard, ...(filterStatus === 'CRITICAL' ? styles.statCardActive : {}) }} onClick={() => setFilterStatus(filterStatus === 'CRITICAL' ? 'ALL' : 'CRITICAL')}>
                  <div style={{ ...styles.statIconBox, background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' }}><span style={{ color: 'white' }}>🚨</span></div>
                  <div style={styles.statContent}><div style={{ ...styles.statNumber, color: '#ea580c' }}>{criticalMeds.length}</div><div style={styles.statTitle}>Critical (≤7d)</div></div>
                </div>
                <div style={{ ...styles.statCard, ...(filterStatus === 'WARNING' ? styles.statCardActive : {}) }} onClick={() => setFilterStatus(filterStatus === 'WARNING' ? 'ALL' : 'WARNING')}>
                  <div style={{ ...styles.statIconBox, background: 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)' }}><span style={{ color: 'white' }}>⚠️</span></div>
                  <div style={styles.statContent}><div style={{ ...styles.statNumber, color: '#ca8a04' }}>{warningMeds.length}</div><div style={styles.statTitle}>Warning (8-30d)</div></div>
                </div>
                <div style={{ ...styles.statCard, ...(filterStatus === 'CAUTION' ? styles.statCardActive : {}) }} onClick={() => setFilterStatus(filterStatus === 'CAUTION' ? 'ALL' : 'CAUTION')}>
                  <div style={{ ...styles.statIconBox, background: 'linear-gradient(135deg, #1880c2 0%, #0369a1 100%)' }}><span style={{ color: 'white' }}>⏳</span></div>
                  <div style={styles.statContent}><div style={{ ...styles.statNumber, color: '#0369a1' }}>{cautionMeds.length}</div><div style={styles.statTitle}>Monitor (31-90d)</div></div>
                </div>
                <div style={{ ...styles.statCard, ...(filterStatus === 'ALL' ? styles.statCardActive : {}), ...(isMobile ? { gridColumn: '1 / -1' } : {}) }} onClick={() => setFilterStatus('ALL')}>
                  <div style={{ ...styles.statIconBox, background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}><span style={{ color: 'white' }}>📦</span></div>
                  <div style={styles.statContent}><div style={{ ...styles.statNumber, color: '#16a34a' }}>{medicines.length}</div><div style={styles.statTitle}>Total Items</div></div>
                </div>
              </>
            )}
          </div>

          <div style={styles.card}>
            <div style={styles.cardBody}>
              <div style={styles.formGrid}>
                <div style={styles.searchWrapper}>
                  <span style={styles.searchIcon}>🔍</span>
                  <input type="text" placeholder="Search by name, brand, or supplier..." value={search} onChange={(e) => setSearch(e.target.value)} style={styles.input} onFocus={(e) => e.target.style.borderColor = '#1880c2'} onBlur={(e) => e.target.style.borderColor = '#e1e8ed'} />
                </div>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ ...styles.select, width: isMobile ? '100%' : 'auto' }} onFocus={(e) => e.target.style.borderColor = '#1880c2'} onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}>
                  <option value="ALL">All Status</option><option value="EXPIRED">❌ Expired</option><option value="CRITICAL">🚨 Critical (≤7d)</option><option value="WARNING">⚠️ Warning (8-30d)</option><option value="CAUTION">⏳ Monitor (31-90d)</option>
                </select>
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardHeader}><h3 style={styles.cardTitle}><span>📋</span> Expiring Medicines</h3></div>

            {loading ? (
              isMobile ? (
                <div style={{ padding: 16 }}>{[1, 2, 3, 4, 5].map(i => <SkeletonMobileCard key={i} />)}</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ ...styles.table, minWidth: 900 }}>
                    <thead><tr><th style={styles.th}>Medicine</th><th style={styles.th}>Brand</th><th style={styles.th}>Category</th><th style={styles.th}>Supplier</th><th style={{ ...styles.th, textAlign: 'right' }}>Price</th><th style={{ ...styles.th, textAlign: 'right' }}>Stock</th><th style={styles.th}>Expiry Date</th><th style={{ ...styles.th, textAlign: 'center' }}>Days</th><th style={{ ...styles.th, textAlign: 'center' }}>Status</th></tr></thead>
                    <tbody>{[1, 2, 3, 4, 5, 6].map(i => <SkeletonTableRow key={i} />)}</tbody>
                  </table>
                </div>
              )
            ) : filteredMedicines.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ fontSize: 56, marginBottom: 16, opacity: 0.3 }}>🎉</div>
                <h3 style={{ fontSize: 18, fontWeight: 600, color: '#1e293b', margin: '0 0 8px' }}>{search || filterStatus !== 'ALL' ? 'No medicines match your criteria' : 'No medicines expiring soon!'}</h3>
                <p style={{ margin: 0 }}>{search || filterStatus !== 'ALL' ? 'Try adjusting your search or filters' : 'Your inventory is in great shape'}</p>
              </div>
            ) : isMobile ? (
              <div style={{ padding: 16 }}>{filteredMedicines.map(m => <MedicineCard key={m._id} medicine={m} />)}</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ ...styles.table, minWidth: 900 }}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Medicine</th><th style={styles.th}>Brand</th><th style={styles.th}>Rack Location</th><th style={styles.th}>Supplier</th><th style={{ ...styles.th, textAlign: 'right' }}>Price</th><th style={{ ...styles.th, textAlign: 'right' }}>Stock</th><th style={styles.th}>Expiry Date</th><th style={{ ...styles.th, textAlign: 'center' }}>Days</th><th style={{ ...styles.th, textAlign: 'center' }}>Status</th><th style={{ ...styles.th, textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMedicines.map(m => {
                      const expiryInfo = getExpiryStatus(m.expiryDate);
                      const isLowStock = m.quantity < (m.minStockLevel || 10);
                      const isExpiredRow = expiryInfo.status === 'expired' || expiryInfo.status === 'expired-today' || expiryInfo.status === 'critical';

                      return (
                        <tr 
                          key={m._id} onClick={() => setSelectedMedicine(m)}
                          style={{ cursor: 'pointer', background: isExpiredRow ? '#fef2f2' : 'transparent', transition: 'background 0.2s' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = isExpiredRow ? '#fee2e2' : '#f8fafc'}
                          onMouseLeave={(e) => e.currentTarget.style.background = isExpiredRow ? '#fef2f2' : 'transparent'}
                        >
                          <td style={styles.td}>
                            <div style={{ fontWeight: 700, color: '#1e293b' }}>{m.name}</div>
                            {m.strength && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{m.strength}</div>}
                          </td>
                          <td style={styles.td}>{m.brand || '-'}</td>
                          <td style={{ ...styles.td, fontWeight: 600, color: '#1880c2' }}>{m.rackLocation || m.rackNumber || 'N/A'}</td>
                          <td style={styles.td}>{m.party?.name || '-'}</td>
                          <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>Rs{fmt(m.sellingPricePerUnit || 0)}</td>
                          <td style={{ ...styles.td, textAlign: 'right' }}>
                            <span style={{ fontWeight: 600, color: isLowStock ? '#dc2626' : '#1e293b' }}>{m.quantity} {m.quantityUnit}</span>
                            {isLowStock && <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 600, marginTop: 2 }}>⚠️ Low Stock</div>}
                          </td>
                          <td style={{ ...styles.td, fontWeight: 600 }}>{new Date(m.expiryDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                          <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700, color: expiryInfo.color }}>{Math.abs(expiryInfo.days)}</td>
                          <td style={{ ...styles.td, textAlign: 'center' }}><StatusBadge expiryDate={m.expiryDate} showFull /></td>
                          <td style={{ ...styles.td, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                            <button
                              type="button" onClick={(e) => openReturnModal(m, e)}
                              style={{ padding: '6px 12px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                              title="Return stock to supplier and credit ledger"
                            >
                              ↩️ Return Stock
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {filteredMedicines.length > 0 && !loading && (
              <div style={{ padding: '16px 24px', background: '#f8fafc', borderTop: '1px solid #e0f2fe', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>Showing <strong style={{ color: '#1e293b' }}>{filteredMedicines.length}</strong> medicines</p>
                <p style={{ margin: 0, fontSize: 13, color: '#94a3b8' }}>💡 Click on any row to view details</p>
              </div>
            )}
          </div>

          {medicines.length > 0 && !loading && (
            <div style={styles.tipBanner}><strong>💡 Tip:</strong> Click on any medicine to view full details. Consider running clearance sales or returns on items nearing expiry to minimize losses.</div>
          )}
        </main>

        {selectedMedicine && (
          <div style={styles.modal} onClick={() => setSelectedMedicine(null)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <div>
                  <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>{selectedMedicine.name}</h2>
                  <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>{selectedMedicine.brand || selectedMedicine.category}</p>
                </div>
                <button 
                  onClick={() => setSelectedMedicine(null)}
                  style={{ width: 36, height: 36, borderRadius: 10, border: 'none', background: '#f4f9fd', color: '#64748b', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >×</button>
              </div>

              <div style={styles.modalBody}>
                <div style={{ textAlign: 'center', marginBottom: 24 }}><StatusBadge expiryDate={selectedMedicine.expiryDate} showFull /></div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 16 }}>
                  <div style={{ padding: 12, background: '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Category</div><div style={{ fontSize: 15, color: '#1e293b', fontWeight: 600 }}>{selectedMedicine.category}</div></div>
                  <div style={{ padding: 12, background: '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Strength</div><div style={{ fontSize: 15, color: '#1e293b', fontWeight: 600 }}>{selectedMedicine.strength || '-'}</div></div>
                  <div style={{ padding: 12, background: '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Selling Price</div><div style={{ fontSize: 15, color: '#1e293b', fontWeight: 600 }}>Rs{fmt(selectedMedicine.sellingPricePerUnit || 0)}</div></div>
                  <div style={{ padding: 12, background: '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>MRP</div><div style={{ fontSize: 15, color: '#1e293b', fontWeight: 600 }}>Rs{fmt(selectedMedicine.mrpPerUnit || 0)}</div></div>
                  <div style={{ padding: 12, background: '#f8fafc', borderRadius: 10 }}>
                    <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Current Stock</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: selectedMedicine.quantity < (selectedMedicine.minStockLevel || 10) ? '#dc2626' : '#1e293b' }}>
                      {selectedMedicine.quantity} {selectedMedicine.quantityUnit}
                    </div>
                  </div>
                  <div style={{ padding: 12, background: '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Min Stock Level</div><div style={{ fontSize: 15, color: '#1e293b', fontWeight: 600 }}>{selectedMedicine.minStockLevel || 10}</div></div>
                  <div style={{ padding: 12, background: '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Batch Number</div><div style={{ fontSize: 15, color: '#1e293b', fontWeight: 600 }}>{selectedMedicine.batchNumber || '-'}</div></div>
                  <div style={{ padding: 12, background: '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Rack Location</div><div style={{ fontSize: 15, color: '#1e293b', fontWeight: 600 }}>{selectedMedicine.rackNumber || '-'}</div></div>
                  <div style={{ padding: 12, background: '#f8fafc', borderRadius: 10, gridColumn: isMobile ? 'auto' : '1 / -1' }}><div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Supplier</div><div style={{ fontSize: 15, color: '#1e293b', fontWeight: 600 }}>{selectedMedicine.party?.name || 'Not specified'}</div></div>
                  <div style={{ padding: 12, background: '#f8fafc', borderRadius: 10, gridColumn: isMobile ? 'auto' : '1 / -1' }}><div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Expiry Date</div><div style={{ fontSize: 15, color: '#1e293b', fontWeight: 600 }}>{new Date(selectedMedicine.expiryDate).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div></div>
                  <div style={{ padding: 12, background: '#fef2f2', borderRadius: 10, gridColumn: isMobile ? 'auto' : '1 / -1' }}><div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Stock Value at Risk</div><div style={{ fontSize: 18, color: '#dc2626', fontWeight: 700 }}>Rs{((selectedMedicine.sellingPricePerUnit || 0) * selectedMedicine.quantity).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {returnModalMed && (
          <div style={styles.modal} onClick={() => setReturnModalMed(null)}>
            <div style={{ ...styles.modalContent, maxWidth: 520 }} onClick={e => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <div>
                  <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#1e293b' }}>↩️ Return Stock to Supplier</h2>
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{returnModalMed.name} • Batch: {returnModalMed.batchNumber || 'N/A'}</div>
                </div>
                <button onClick={() => setReturnModalMed(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
              </div>

              <form onSubmit={handleConfirmReturn} style={styles.modalBody}>
                <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, marginBottom: 16, border: '1px solid #e0f2fe' }}>
                  <div style={{ fontSize: 12, color: '#64748b' }}>Current Stock: <strong style={{ color: '#1e293b' }}>{returnModalMed.quantity} {returnModalMed.quantityUnit || 'units'}</strong></div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Expiry Date: <strong style={{ color: '#dc2626' }}>{new Date(returnModalMed.expiryDate).toLocaleDateString('en-IN')}</strong></div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Rack Location: <strong style={{ color: '#1880c2' }}>{returnModalMed.rackLocation || 'N/A'}</strong></div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Select Supplier (Party) *</label>
                    <select
                      value={returnParty}
                      onChange={e => setReturnParty(e.target.value)}
                      style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                      required
                      onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                      onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                    >
                      <option value="">-- Select Supplier --</option>
                      {parties.map(p => (
                        <option key={p._id} value={p._id}>
                          {p.name} - ({p.registrationType === 'VAT Registered' ? 'VAT' : p.registrationType === 'PAN Registered' ? 'PAN' : 'Unreg.'})
                        </option>
                      ))}
                    </select>
                    <span style={{ fontSize: 11, color: '#64748b' }}>Return will be marked as Pending until Vendor Approval</span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Return Quantity *</label>
                      <input
                        type="number" min="1" max={returnModalMed.quantity} value={returnQty} onChange={e => setReturnQty(e.target.value)}
                        style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }} required
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Cost Price (Rs) *</label>
                      <input
                        type="number" step="0.01" min="0" value={returnPrice} onChange={e => setReturnPrice(e.target.value)}
                        style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }} required
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>VAT/GST Reversal</label>
                    <select
                      value={returnTaxPercent}
                      onChange={e => setReturnTaxPercent(e.target.value)}
                      disabled={!isReturnVatAllowed}
                      style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, background: isReturnVatAllowed ? '#fff' : '#f1f5f9' }}
                    >
                      <option value="0">0% (Exempt)</option>
                      <option value="13">13% VAT</option>
                    </select>
                    {!isReturnVatAllowed && returnParty && (
                      <span style={{ fontSize: 11, color: '#dc2626' }}>*VAT disabled for {selectedReturnSupplier?.registrationType} supplier</span>
                    )}
                  </div>

                  <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#64748b' }}>
                      <span>Base Refund:</span> <span>Rs {fmt(returnSubtotal)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#64748b' }}>
                      <span>VAT Reversal:</span> <span>Rs {fmt(returnTaxAmount)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#166534', fontWeight: 700, borderTop: '1px dashed #cbd5e1', paddingTop: 8 }}>
                      <span>Total Expected Credit Note:</span> <span>Rs {fmt(returnGrandTotal)}</span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Notes / Reason</label>
                    <input
                      type="text" value={returnNotes} onChange={e => setReturnNotes(e.target.value)}
                      style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                      placeholder="e.g. Returned expired stock for credit memo"
                    />
                  </div>
                </div>

                <div style={{ background: '#e0f2fe', padding: 12, borderRadius: 8, marginTop: 16, border: '1px solid #bae6fd' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#0369a1', marginBottom: 4 }}>📄 Single-Page Return Report</div>
                  <div style={{ fontSize: 12, color: '#0369a1' }}>
                    An optimized single-page return report will be generated, saved to history, and opened for printing. 
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24, paddingTop: 16, borderTop: '1px solid #e0f2fe' }}>
                  <button type="button" onClick={() => setReturnModalMed(null)} style={styles.btnSecondary}>Cancel</button>
                  <button type="submit" disabled={submittingReturn} style={{ ...styles.btnSecondary, background: '#dc2626', color: '#fff', borderColor: '#dc2626' }}>
                    {submittingReturn ? 'Processing...' : '📋 Request Return & Print Note'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}